import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from 'app/auth/auth.service';
import { ConfirmationDialogueComponent } from 'app/components/application/common/confirmation-dialogue/confirmation-dialogue.component';
import {
  AdminService,
  DocumentMigrationPhase,
  IDocumentMigrationJob,
  IDocumentMigrationStatus
} from 'app/services/admin/admin.service';
import { ToastrService } from 'ngx-toastr';
import { Subject, interval } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-admin-functions',
  templateUrl: './admin-functions.component.html',
  styleUrls: ['./admin-functions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminFunctionsComponent implements OnInit, OnDestroy {
  isAdmin = false;
  loading = false;
  status: IDocumentMigrationStatus = null;
  history: IDocumentMigrationJob[] = [];

  private readonly destroyed$ = new Subject<void>();

  constructor(
    private readonly adminService: AdminService,
    private readonly authService: AuthService,
    private readonly dialog: MatDialog,
    private readonly toastr: ToastrService,
    private readonly changeDetector: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.isAdmin = this.authService.userHasAtLeastOneRole(['Admin']);

    if (!this.isAdmin) {
      return;
    }

    this.refresh();

    interval(3000)
      .pipe(
        switchMap(() => this.adminService.getDocumentMigrationStatus()),
        takeUntil(this.destroyed$))
      .subscribe({
        next: status => this.applyStatus(status),
        error: () => { /* transient polling failures are reported by the manual refresh */ }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  get job(): IDocumentMigrationJob {
    return this.status?.latestJob ?? null;
  }

  get isRunning(): boolean {
    return this.status?.isRunning === true || this.job?.status === 'Running';
  }

  get progressPercent(): number {
    if (!this.job || !this.job.totalCount) {
      return 0;
    }

    return Math.round((this.job.processedCount / this.job.totalCount) * 100);
  }

  /** Verify is only meaningful once a copy has completed, and reclaim only once verify is clean. */
  canStart(phase: DocumentMigrationPhase): boolean {
    if (this.isRunning) {
      return false;
    }

    if (phase === 'Copy') {
      return true;
    }

    const completedPhases = this.history.filter(h => h.status === 'Completed').map(h => h.phase);

    if (phase === 'Verify') {
      return completedPhases.includes('Copy');
    }

    return completedPhases.includes('Verify');
  }

  start(phase: DocumentMigrationPhase): void {
    const messages: Record<DocumentMigrationPhase, string> = {
      Copy: 'This copies every document currently stored in the database into Azure Blob Storage and creates the matching Document rows.\n\nIt is safe to run more than once - rows that are already migrated are skipped.',
      Verify: 'This re-reads every migrated blob and compares its SHA-256 hash against the original database bytes.\n\nNo data is changed.',
      Reclaim: 'This permanently clears the file bytes from the legacy DocumentFile table.\n\nOnly run this after a clean verify and a full database backup.'
    };

    this.dialog
      .open(ConfirmationDialogueComponent, {
        width: '520px',
        data: { title: `Run ${phase}?`, message: messages[phase] }
      })
      .afterClosed()
      .subscribe(confirmed => {
        if (!confirmed) {
          return;
        }

        this.adminService.startDocumentMigration(phase).subscribe({
          next: () => {
            this.toastr.success(`${phase} started.`);
            this.refresh();
          },
          error: error => this.toastr.error(error?.error ?? `Unable to start ${phase}.`)
        });
      });
  }

  cancel(): void {
    this.adminService.cancelDocumentMigration().subscribe({
      next: () => this.toastr.info('Cancellation requested.'),
      error: () => this.toastr.error('Unable to cancel the migration.')
    });
  }

  downloadFailureReport(): void {
    if (!this.job) {
      return;
    }

    this.adminService.downloadFailureReport(this.job.id).subscribe({
      next: blob => {
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `document-migration-failures-${this.job.id}.csv`;
        anchor.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => this.toastr.error('Unable to download the failure report.')
    });
  }

  refresh(): void {
    this.loading = true;

    this.adminService.getDocumentMigrationStatus().subscribe({
      next: status => {
        this.loading = false;
        this.applyStatus(status);
      },
      error: error => {
        this.loading = false;
        this.changeDetector.markForCheck();
        this.toastr.error(error?.error ?? 'Unable to load the migration status.');
      }
    });
  }

  private applyStatus(status: IDocumentMigrationStatus): void {
    this.status = status;
    this.changeDetector.markForCheck();

    this.adminService.getDocumentMigrationHistory().subscribe(history => {
      this.history = history;
      this.changeDetector.markForCheck();
    });
  }
}
